import { ZButton } from "@/shared/components/button";
import ZFileName from "@/shared/components/custom/file-upload/FileName";
import { ZProgress } from "@/shared/components/progress";
import { ZTooltip } from "@/shared/components/ZTooltip/ZTooltip";
import { fileErrorTypes } from "@/shared/constants";
import { getSignedUrl } from "@/shared/requests/get-signed-url";
import { uploadFileReq } from "@/shared/requests/upload-file";
import { addNotification } from "@/shared/states/notification";
import { useIsDemoActive } from "@/shared/states/user";
import type { UploadedFile, UploadProgress } from "@/shared/types/file-upload";
import { cn } from "@/shared/utils/classname-merger";
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Spinner } from "@nextui-org/react";
import { AxiosProgressEvent } from "axios";
import { Check, ChevronLeft, ChevronRight, File, RotateCw, Trash, Upload } from "lucide-react";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

interface UploadDocumentsModalProps {
  isOpen: boolean;
  onClose: () => void;
  controlId: string;
  controlName: string;
  controlDescription: string;
  onPrevControl?: () => void;
  onNextControl?: () => void;
  currentIndex?: number;
  totalControls?: number;
  sourceUrls: string[];
  fileNames: string[];
  onUpdateSourceUrls?: (urls: string[], fileNames?: string[]) => void;
  showSaveSuccess?: boolean;
  maxFilesAllowed?: number;
}

const UploadDocumentsModal = ({
  isOpen,
  onClose,
  controlId,
  controlName,
  controlDescription,
  onPrevControl,
  onNextControl,
  currentIndex,
  totalControls,
  sourceUrls,
  fileNames,
  onUpdateSourceUrls,
  showSaveSuccess = false,
  maxFilesAllowed = 30,
}: UploadDocumentsModalProps) => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [existingFiles, setExistingFiles] = useState<{ fileName: string, url: string }[]>([]);
  const [filesToRemove, setFilesToRemove] = useState<string[]>([]);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({});
  const [hasValidationError, setHasValidationError] = useState(false);
  const [hasValidFiles, setHasValidFiles] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success'>('idle');
  const inputRef = useRef<HTMLInputElement>(null);
  const prevControlId = useRef(controlId);
  const prevSourceUrlsLength = useRef(sourceUrls.length);
  const isDemo = useIsDemoActive();
  
  // Add AbortController refs to track ongoing operations
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  const uploadPromisesRef = useRef<Map<string, Promise<void>>>(new Map());
  
  // Cleanup function to abort all ongoing operations
  const cleanup = () => {
    // Abort all ongoing uploads
    abortControllersRef.current.forEach((controller) => {
      if (!controller.signal.aborted) {
        controller.abort();
      }
    });
    abortControllersRef.current.clear();
    uploadPromisesRef.current.clear();
    setUploadProgress({});
  };
  
  // Cleanup on unmount or when modal closes
  useEffect(() => {
    if (!isOpen) {
      cleanup();
    }
    return () => cleanup();
  }, [isOpen]);

  // Sync existingFiles state with sourceUrls prop
  useEffect(() => {
    // Update when:
    // - modal opens
    // - control changes (arrows clicked)
    // - sourceUrls change (files added or removed)
    if (isOpen && (
      prevControlId.current !== controlId ||
      sourceUrls.length !== prevSourceUrlsLength.current ||
      existingFiles.length !== sourceUrls.length
    )) {
      if (sourceUrls && sourceUrls.length > 0) {
        const fileList = sourceUrls.map((url, index) => {
          // Use provided fileName if available, otherwise extract from URL or use generic name
          const fileName = fileNames[index] || url.split('/').pop() || `File ${index + 1}`;
          return { fileName, url };
        });
        setExistingFiles(fileList);
      } else {
        setExistingFiles([]);
      }

      // Reset files state when control changes to prevent files from appearing in other controls
      if (prevControlId.current !== controlId) {
        setFiles([]);
        setFilesToRemove([]);
        setUploadProgress({});
      }

      prevControlId.current = controlId;
      prevSourceUrlsLength.current = sourceUrls.length;
    } else if (!isOpen) {
      // Reset upload state when modal closes
      setFiles([]);
      setFilesToRemove([]);
      setUploadProgress({});
    }
  }, [isOpen, sourceUrls, fileNames, controlId, sourceUrls.length]);

  // Check validation status whenever files change
  useEffect(() => {
    const hasErrors = files.some(file => doesFileHaveError(file));
    const hasValid = files.some(file =>
      file.status === "success" ||
      file.status === "inprogress" ||
      file.status === "gettingurl"
    );

    setHasValidationError(hasErrors);
    setHasValidFiles(hasValid);
  }, [files]);

  const isFileTooLarge = (file: File, megaBytes = 20) => {
    const sizeLimit = megaBytes * 1024 * 1024;
    return file.size > sizeLimit;
  };

  const handleFiles = async (uploadedFiles: FileList) => {
    setIsUploading(true);
    const remainingFileLimit = maxFilesAllowed - sourceUrls.length;
    let counter = files.filter(f => !fileErrorTypes.has(f.status)).length;
    let shouldShowFileTooLargeNotification = false;
    const largeFileNames: string[] = [];

    const newFiles: UploadedFile[] = [];

    // Filter for allowed file types
    const allowedFileTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "application/json",
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/png",
      "image/jpeg",
      "image/jpg",
      "text/csv",
    ];

    const validFilesTypes = Array.from(uploadedFiles).filter(file => allowedFileTypes.includes(file.type));

    for (const file of validFilesTypes) {
      let alreadyAdded = false;
      if (counter >= remainingFileLimit) {
        newFiles.push({
          id: uuidv4(),
          file,
          preview: URL.createObjectURL(file),
          status: "too_many_files",
          progressBar: false,
        });
        alreadyAdded = true;
      }

      const fileTooLarge = isFileTooLarge(file);
      shouldShowFileTooLargeNotification = shouldShowFileTooLargeNotification || fileTooLarge;

      if (fileTooLarge) {
        largeFileNames.push(file.name);
      } else {
        counter = counter + 1;
      }

      if (!alreadyAdded) {
        newFiles.push({
          id: uuidv4(),
          file,
          preview: URL.createObjectURL(file),
          status: fileTooLarge ? "too_large" : "gettingurl",
          progressBar: false,
        });
      }
    }

    // Check for duplicate filenames among currently uploading files
    const fileName = new Set<string>(files.map((oldFile) => oldFile.file.name));

    // Also add existing filenames to check duplicates against previously uploaded files
    // But exclude files that are marked for removal
    const remainingExistingFiles = existingFiles.filter(file => !filesToRemove.includes(file.url));
    const existingFileNames = new Set<string>(remainingExistingFiles.map(file => file.fileName));

    for (const file of newFiles) {
      if (fileName.has(file.file.name)) {
        if (file.status !== "too_many_files") {
          file.status = "duplicate_files";
        }
      } else {
        fileName.add(file.file.name);
      }

      // Check for duplicates against existing files (excluding those marked for removal)
      if (existingFileNames.has(file.file.name) && file.status !== "too_many_files") {
        file.status = "duplicate_files";
      }
    }

    if (maxFilesAllowed && counter > remainingFileLimit) {
      addNotification({
        type: "warn",
        title: "Too many files!",
        message: `You can only upload up to ${maxFilesAllowed} files for this section. Please remove ${counter - remainingFileLimit} files to proceed.`,
      });
    }

    if (shouldShowFileTooLargeNotification) {
      addNotification({
        type: "error",
        title: "File size limit exceeded!",
        message:
          largeFileNames.length > 1
            ? `${largeFileNames.length} files exceed the maximum file size of 20mb. Please remove them to proceed.`
            : `The file ${largeFileNames[0]} exceeds the maximum file size of 20mb. Please remove it to proceed.`,
      });
    }

    const validFilesArray = newFiles.filter(
      (fileObj: UploadedFile) => fileObj.status === "gettingurl"
    );
    const invalidFilesArray = newFiles.filter(
      (fileObj: UploadedFile) => fileObj.status !== "gettingurl"
    );

    // Process valid files (get signed URLs and upload)
    await Promise.all(
      validFilesArray.map(async (fileObj) => {
        const controller = new AbortController();
        abortControllersRef.current.set(fileObj.file.name, controller);
        
        const uploadPromise = (async () => {
          try {
            // Check if already aborted
            if (controller.signal.aborted) return;
            
            const url = await getSignedUrl({
              file_names: [fileObj.file.name],
              ...(isDemo ? { max_age: 120 } : {}),
            });
            
            // Check if aborted after getting signed URL
            if (controller.signal.aborted) return;
            
            fileObj.signedUrl = url[0];
            fileObj.status = "inprogress";
            setFiles((prevFiles) => [
              ...prevFiles,
              {
                ...fileObj,
                status: prevFiles.find(
                  (fil) => fil.file.name === fileObj.file.name
                )
                  ? "duplicate_files" as const
                  : fileObj.status,
              },
            ]);
            setUploadProgress((prevProgress) => ({
              ...prevProgress,
              [fileObj.file.name]: 0,
            }));

            await uploadFile(fileObj, controller);
          } catch (e) {
            // Don't show error if operation was aborted
            if (!controller.signal.aborted) {
              console.error(e);
              setFiles((prevFiles) => [
                ...prevFiles,
                { ...fileObj, status: "failed" as const },
              ]);
              setUploadProgress((prevProgress) => {
                const newProgress = { ...prevProgress };
                delete newProgress[fileObj.file.name];
                return newProgress;
              });
            }
          } finally {
            // Clean up controller reference
            abortControllersRef.current.delete(fileObj.file.name);
            uploadPromisesRef.current.delete(fileObj.file.name);
          }
        })();
        
        uploadPromisesRef.current.set(fileObj.file.name, uploadPromise);
        return uploadPromise;
      })
    );

    // Add invalid files to the state
    setFiles((prev) => [...prev, ...invalidFilesArray]);
    setIsUploading(false);
  };

  const uploadFile = async (fileObj: UploadedFile, controller?: AbortController) => {
    const name = fileObj.file.name;
    if (!fileObj.signedUrl) {
      return;
    }
    
    // Use provided controller or create a new one
    const abortController = controller || new AbortController();
    if (!controller) {
      abortControllersRef.current.set(name, abortController);
    }
    
    try {
      // Check if already aborted
      if (abortController.signal.aborted) return;
      
      await uploadFileReq(
        fileObj.signedUrl,
        fileObj.file,
        (progressEvent: AxiosProgressEvent) => {
          // Don't update progress if aborted
          if (abortController.signal.aborted) return;
          
          if (progressEvent.total) {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress((prevProgress) => ({
              ...prevProgress,
              [name]: progress,
            }));
          }
        },
        abortController.signal
      );
      
      // Don't update state if aborted
      if (abortController.signal.aborted) return;
      
      setUploadProgress((prevProgress) => ({
        ...prevProgress,
        [name]: -1,
      }));
      setFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.file.name === name
            ? {
              ...f,
              status:
                f.status === "duplicate_files"
                  ? "duplicate_files"
                  : "success" as const,
            }
            : f
        )
      );
    } catch (error) {
      // Don't handle error if operation was aborted
      if (abortController.signal.aborted) return;
      
      console.error(error);
      setUploadProgress((prevProgress) => ({
        ...prevProgress,
        [name]: 100,
      }));

      setTimeout(() => {
        if (!abortController.signal.aborted) {
          setFiles((prevFiles) =>
            prevFiles.map((f) =>
              f.file.name === name ? { ...f, status: "failed" as const } : f
            )
          );
          setUploadProgress((prevProgress) => ({
            ...prevProgress,
            [name]: -1,
          }));
        }
      }, 1000);
    } finally {
      // Clean up controller reference
      if (!controller) { // Only clean up if we created it here
        abortControllersRef.current.delete(name);
        uploadPromisesRef.current.delete(name);
      }
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (files) {
      void handleFiles(files);
      e.target.value = "";
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const { files } = e.dataTransfer;
    void handleFiles(files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.add("bg-[#F9FAFB]");
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove("bg-[#F9FAFB]");
  };

  const handleRetry = async (fileObj: UploadedFile) => {
    // Cancel any existing upload for this file
    const existingController = abortControllersRef.current.get(fileObj.file.name);
    if (existingController) {
      existingController.abort();
    }
    
    const controller = new AbortController();
    abortControllersRef.current.set(fileObj.file.name, controller);
    
    const retryPromise = (async () => {
      try {
        setIsUploading(true);
        fileObj.status = "retrying";
        setFiles((prevFiles) => [
          ...prevFiles.map((f) => (f.id === fileObj.id ? { ...fileObj } : f)),
        ]);
        
        if (controller.signal.aborted) return;
        
        const url = await getSignedUrl({
          file_names: [fileObj.file.name],
          ...(isDemo ? { max_age: 120 } : {}),
        });
        
        if (controller.signal.aborted) return;
        
        fileObj.signedUrl = url[0];
        fileObj.status = "inprogress";
        setFiles((prevFiles) => [
          ...prevFiles.map((f) => (f.id === fileObj.id ? { ...fileObj } : f)),
        ]);
        setUploadProgress((prevProgress) => ({
          ...prevProgress,
          [fileObj.file.name]: 0,
        }));

        await uploadFile(fileObj, controller);
        if (!controller.signal.aborted) {
          setIsUploading(false);
        }
      } catch (e) {
        if (!controller.signal.aborted) {
          console.error(e);
          setFiles((prevFiles) =>
            prevFiles.map((f) =>
              f.id === fileObj.id ? { ...fileObj, status: "failed" as const } : f
            )
          );
          addNotification({
            message: "Error in uploading the file",
            type: "error",
          });
          setUploadProgress((prevProgress) => {
            const newProgress = { ...prevProgress };
            delete newProgress[fileObj.file.name];
            return newProgress;
          });
          setIsUploading(false);
        }
      } finally {
        abortControllersRef.current.delete(fileObj.file.name);
        uploadPromisesRef.current.delete(fileObj.file.name);
      }
    })();
    
    uploadPromisesRef.current.set(fileObj.file.name, retryPromise);
  };

  const handleRemoveFile = (fileId: string) => {
    const fileToRemove = files.find(f => f.id === fileId);
    if (!fileToRemove) return;

    setFiles((prevFiles) => {
      const filteredFiles = prevFiles.filter(f => f.id !== fileId);

      // Track which duplicate files need to be made valid
      // We only promote one duplicate file per deleted file name
      let removeDuplicateFileFlag = true;
      const convertedFileNames = new Set<string>();

      let validCount = filteredFiles.filter(f => !fileErrorTypes.has(f.status)).length;

      const fileNameSet = new Set<string>();
      
      // Files that need signed URL after status update
      const filesNeedingSignedUrl: UploadedFile[] = [];

      // Convert duplicate files to valid if the original is deleted
      // First pass - handle duplicates and excess files
      const processedFiles = filteredFiles.map(file => {
        let status = file.status;

        if (isFileTooLarge(file.file)) {
          status = "too_large" as const;
        } else if (
          file.status === "duplicate_files" &&
          fileToRemove.file.name === file.file.name &&
          !convertedFileNames.has(file.file.name) &&
          removeDuplicateFileFlag
        ) {
          status = "success" as const;
          convertedFileNames.add(file.file.name);
          removeDuplicateFileFlag = false;
          validCount++;
        } else if (file.status === "too_many_files" && validCount < maxFilesAllowed) {
          status = "success" as const;
          validCount++;
        }

        if (fileNameSet.has(file.file.name)) {
          if (status !== "too_large" && status !== "too_many_files" && status !== "success") {
            status = "duplicate_files" as const;
          }
        } else if (status !== "too_large" && status !== "too_many_files" && status !== "duplicate_files") {
          fileNameSet.add(file.file.name);
        }

        // If now valid and missing signedUrl, mark for upload
        if (
          status === "success" &&
          !file.signedUrl &&
          !["too_large", "too_many_files", "duplicate_files", "failed", "already_exists"].includes(status)
        ) {
          const updatedStatus = "gettingurl" as const;
          filesNeedingSignedUrl.push({ ...file, status: updatedStatus });
          status = updatedStatus;
        }

        return { ...file, status };
      });

      // Recalculate available slots
      const currentValidCount = processedFiles.filter(f =>
        f.status === "success" ||
        f.status === "inprogress" ||
        f.status === "gettingurl" ||
        f.status === "retrying"
      ).length + existingFiles.length;

      const availableSlots = maxFilesAllowed - currentValidCount;

      // Convert "too_many_files" to "success" if there's room
      // BUT never convert files that are too large
      let updatedAvailableSlots = availableSlots;

      const updatedFiles = processedFiles.map(file => {
        // If this is an excess file and we have slots available, mark it as success
        // BUT never promote files that are too large
        if (
          file.status === "too_many_files" &&
          isFileTooLarge(file.file) !== true &&
          updatedAvailableSlots > 0
        ) {
          updatedAvailableSlots--;
          if (!file.signedUrl) {
            const updatedFile = { ...file, status: "gettingurl" as const };
            filesNeedingSignedUrl.push(updatedFile);
            return updatedFile;
          } else {
            return { ...file, status: "success" as const };
          }
        }
        return file;
      });

      // After state update, trigger upload for files needing signed URL
      setTimeout(() => {
        filesNeedingSignedUrl.forEach(async (fileObj) => {
          try {
            setIsUploading(true);
            const url = await getSignedUrl({
              file_names: [fileObj.file.name],
              ...(isDemo ? { max_age: 120 } : {}),
            });
            fileObj.signedUrl = url[0];
            fileObj.status = "inprogress";
            setFiles((prev) =>
              prev.map((f) =>
                f.id === fileObj.id ? { ...fileObj } : f
              )
            );
            setUploadProgress((prev) => ({
              ...prev,
              [fileObj.file.name]: 0,
            }));
            await uploadFile(fileObj);
            setIsUploading(false);
          } catch (e) {
            console.error(e);
            setFiles((prev) =>
              prev.map((f) =>
                f.id === fileObj.id
                  ? { ...fileObj, status: "failed" }
                  : f
              )
            );
            setUploadProgress((prev) => {
              const newProgress = { ...prev };
              delete newProgress[fileObj.file.name];
              return newProgress;
            });
            setIsUploading(false);
          }
        });
      }, 0);

      return updatedFiles;
    });

    // We don't need to handle signed URL deletion here as that's typically 
    // managed when saving the form
  };

  const handleRemoveExistingFile = (urlToRemove: string) => {
    // Instead of immediately updating the control, just mark the file for removal
    // and filter it out from the display
    setFilesToRemove(prev => [...prev, urlToRemove]);

    // After marking file for removal, check if any excess files can be promoted
    setFiles(prevFiles => {
      // Calculate available slots based on existing files that will remain after save
      const remainingExistingFiles = existingFiles.filter(file =>
        !filesToRemove.includes(file.url) && file.url !== urlToRemove
      );

      const currentValidCount = prevFiles.filter(f =>
        f.status === "success" ||
        f.status === "inprogress" ||
        f.status === "gettingurl" ||
        f.status === "retrying"
      ).length + remainingExistingFiles.length;

      const availableSlots = maxFilesAllowed - currentValidCount;

      // Convert "too_many_files" to "success" if there's room
      // BUT never convert too_large files
      let updatedAvailableSlots = availableSlots;

      return prevFiles.map(file => {
        // If this is an excess file and we have slots available, mark it as success
        // BUT never mark too_large files as valid
        if (
          file.status === "too_many_files" &&
          isFileTooLarge(file.file) !== true &&  // Never change too_large files 
          updatedAvailableSlots > 0
        ) {
          updatedAvailableSlots--;
          return { ...file, status: "success" };
        }
        return file;
      });
    });
  };

  const handleUpload = () => {
    if (files.length === 0 && filesToRemove.length === 0) {
      addNotification({
        type: "error",
        title: "No changes to save",
        message: "Please add files or select files to remove",
      });
      return;
    }

    // Extract information from successfully uploaded files
    const successfulFiles = files.filter(file => file.status === "success");
    const newUrls = successfulFiles.map(file => file.signedUrl || '').filter(url => url);
    const newFileNames = successfulFiles.map(file => file.file.name || '').filter(name => name);

    // Get existing URLs, excluding those marked for removal
    const remainingExistingFiles = existingFiles.filter(file => !filesToRemove.includes(file.url));
    const existingUrls = remainingExistingFiles.map(file => file.url);

    // Filter out any URLs that are already in remainingExistingFiles
    const uniqueNewUrls = newUrls.filter(url => !existingUrls.includes(url));
    const uniqueNewFileNames = newFileNames.filter((_, index) =>
      !existingUrls.includes(newUrls[index])
    );

    // Update the existingFiles state immediately to prevent flicker
    setExistingFiles(remainingExistingFiles);

    // Call the update callback with combined URLs and fileNames (existing files that remain + new files)
    if (onUpdateSourceUrls) {
      onUpdateSourceUrls(
        [...existingUrls, ...uniqueNewUrls],
        [...remainingExistingFiles.map(file => file.fileName), ...uniqueNewFileNames]
      );
      if (showSaveSuccess) {
        setSaveStatus('success');
        setTimeout(() => {
          setSaveStatus('idle');
        }, 3000);
      }
    }

    // Reset the files to remove state
    setFilesToRemove([]);

    // Just close the modal immediately - don't show a loading state or clear files
    // This prevents the shaking/rerendering effect
    !showSaveSuccess && handleModalClose();
    setFiles([]);
  };
  
  // Add method to cancel all uploads (can be exposed via ref if needed)
  const cancelAllUploads = () => {
    cleanup();
    // Update file statuses to cancelled
    setFiles((prevFiles) =>
      prevFiles.map((file) => ({
        ...file,
        status: file.status === "inprogress" || file.status === "gettingurl" 
          ? "failed" as const 
          : file.status,
      }))
    );
  };

  const doesFileHaveError = (fileObj: UploadedFile) => {
    return fileErrorTypes.has(fileObj.status)
  };

  // Determine if the save button should be enabled
  const isSaveButtonEnabled = () => {
    // Enable if there are valid files to save OR files marked for removal
    const hasFilesToSave = hasValidFiles && !hasValidationError && !isUploading;
    const hasFilesToDelete = filesToRemove.length > 0;

    return hasFilesToSave || hasFilesToDelete;
  };

  const getFileNameSuffix = (fileObj: UploadedFile) => {
    let fileNameSuffix = "";

    switch (fileObj.status) {
      case "too_large":
        fileNameSuffix = " (too large)";
        break;
      case "too_many_files":
        fileNameSuffix = " (Excess file. Remove any.)";
        break;
      case "duplicate_files":
        fileNameSuffix = " (duplicate file)";
        break;
      default:
        break;
    }

    return fileNameSuffix;
  };

  // Render a file item UI to match the FileUpload component
  const renderFileItem = (fileObj: UploadedFile, index: number) => {
    return (
      <div key={index}>
        <div className="flex items-center justify-between gap-2 mt-0">
          <File
            size={16}
            className="ml-3 min-w-4"
            color={
              fileObj.status === "success"
                ? "#22c55e"
                : fileObj.status === "inprogress" ? "#000" : "#e7000b"
            }
          />
          <div className="flex-1 flex flex-col justify-center">
            <div className="flex items-center gap-1">
              <p
                className={cn(
                  "text-sm cursor-pointer hover:underline max-w-full",
                  fileObj.status === "success"
                    ? "text-green-600"
                    : doesFileHaveError(fileObj)
                      ? "text-red-600"
                      : "text-black"
                )}
              >
                <ZFileName filename={fileObj.file.name} />
              </p>
              <p className={cn(
                "text-sm cursor-pointer hover:underline max-w-full whitespace-nowrap",
                fileObj.status === "success"
                  ? "text-green-600"
                  : doesFileHaveError(fileObj)
                    ? "text-red-600"
                    : "text-black"
              )}>
                {getFileNameSuffix(fileObj)}
              </p>
              {fileObj.status === "gettingurl" ? <Spinner size="sm" /> : null}
            </div>
            <div>
              {fileObj.status !== "success" &&
                !isNaN(uploadProgress[fileObj.file.name]) &&
                uploadProgress[fileObj.file.name] !== -1 ? (
                <ZProgress
                  aria-label="file-upload-progress"
                  value={uploadProgress[fileObj.file.name] || 0}
                  maxValue={100}
                  className={cn("h-[3px] mt-1 [&>*]:" + "bg-blue-600")}
                />
              ) : (
                <></>
              )}
            </div>
          </div>
          {fileObj.status === "failed" || fileObj.status === "retrying" ? (
            <ZButton
              isIconOnly
              className="bg-transparent"
              onClick={() => void handleRetry(fileObj)}
              isLoading={fileObj.status === "retrying"}
            >
              <RotateCw size={16} color="gray" />
            </ZButton>
          ) : null}
          <ZButton
            isIconOnly
            className="bg-transparent"
            onClick={() => handleRemoveFile(fileObj.id)}
          >
            <Trash size={16} color="gray" />
          </ZButton>
        </div>
      </div>
    );
  };

  // Render an existing file item in the same style as new files
  const renderExistingFileItem = (file: { fileName: string, url: string }, index: number) => {
    const isMarkedForRemoval = filesToRemove.includes(file.url);

    // Don't render files that are marked for removal
    if (isMarkedForRemoval) {
      return null;
    }

    return (
      <div key={`existing-${index}`}>
        <div className="flex items-center justify-between gap-2 mt-0">
          <File size={16} className="ml-3 min-w-4" color="#22c55e" />
          <div className="flex-1 flex flex-col justify-center">
            <div className="flex items-center gap-1">
              <p className="text-sm cursor-pointer hover:underline max-w-full text-green-600">
                <ZFileName filename={file.fileName} />
              </p>
            </div>
          </div>
          <ZButton
            isIconOnly
            className="bg-transparent"
            onClick={() => handleRemoveExistingFile(file.url)}
          >
            <Trash size={16} color="gray" />
          </ZButton>
        </div>
      </div>
    );
  };

  // Handle modal close with cleanup
  const handleModalClose = () => {
    cleanup();
    onClose();
  };
  
  return (
    <Modal isOpen={isOpen} onClose={handleModalClose} size="lg" style={{ height: '84vh' }}>
      <ModalContent>
        <ModalHeader className="flex flex-col pb-2 px-6 border-b">
          <div className="text-center mb-3">
            <div className="text-lg leading-7 text-center text-[#11181C] font-semibold font-inter">
              Upload Documents
            </div>
          </div>

          <div className="flex items-center w-full">
            <ZButton
              isIconOnly
              size="sm"
              variant="light"
              onClick={onPrevControl}
              isDisabled={!onPrevControl}
              className="p-0 min-w-0 w-6 h-6 flex-shrink-0"
            >
              <ChevronLeft size={16} />
            </ZButton>

            <div className="flex-grow px-2 w-full overflow-hidden">
              <ZTooltip showArrow={true} content={controlName} placement="bottom">
                <div className="text-sm leading-5 text-center text-[#3F3F46] font-medium font-inter truncate">
                  {controlName}
                </div>
              </ZTooltip>
            </div>

            <ZButton
              isIconOnly
              size="sm"
              variant="light"
              onClick={onNextControl}
              isDisabled={!onNextControl}
              className="p-0 min-w-0 w-6 h-6 flex-shrink-0"
            >
              <ChevronRight size={16} />
            </ZButton>
          </div>

          <ZTooltip showArrow={true} content={controlDescription} placement="bottom">
            <div className="text-xs leading-4 mt-2 text-center text-[#777780] font-normal font-inter truncate">
              {controlDescription}
            </div>
          </ZTooltip>
        </ModalHeader>

        <ModalBody className="pt-4 flex flex-col" style={{ overflow: 'hidden' }}>
          {/* Drag and drop upload section */}
          <div
            onClick={() => inputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            className="bg-[#F7F7FA] p-2 flex items-center flex-col justify-center h-32 w-full border-dashed border-[1px] border-gray-300 rounded-md cursor-pointer hover:bg-gray-50"
          >
            <Upload size={24} />
            <label className="cursor-pointer text-sm mt-4">
              Drag and drop files here or{" "}
              <span className="text-[#006FEE]">browse your computer</span>
            </label>
            <p className="text-xs text-center text-gray-500 mt-2 max-w-80">
              (Accepted files: Up to {Math.max(0, maxFilesAllowed - (sourceUrls.length - filesToRemove.length + files.filter(f =>
                f.status === "success" || f.status === "inprogress" || f.status === "gettingurl" || f.status === "retrying"
              ).length))} files of Excel, JSON, PDF, Word, PNG, JPEG, CSV of up to 20mb)
            </p>

            <input
              type="file"
              ref={inputRef}
              multiple={true}
              onChange={handleFileChange}
              className="hidden"
              id="file-input"
              accept={[
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                "application/vnd.ms-excel",
                "application/json",
                "application/pdf",
                "application/msword",
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                "image/png",
                "image/jpeg",
                "image/jpg",
                "text/csv",
              ].join(",")}
            />
          </div>

          {/* All files section - both newly uploaded and existing */}
          <div className="mt-1 flex-grow overflow-y-auto">
            {/* Show error files first */}
            {files.filter(doesFileHaveError).map((file, index) => renderFileItem(file, index))}

            {/* Then show valid files */}
            {files.filter(file => !doesFileHaveError(file)).map((file, index) => renderFileItem(file, index))}

            {/* Show existing files at the bottom */}
            {existingFiles.map((file, index) => renderExistingFileItem(file, index))}
          </div>
        </ModalBody>
        <ModalFooter className="border-t pt-4">
          <ZButton color="default" variant="bordered" className="min-w-[80px]" onClick={handleModalClose}>
            Cancel
          </ZButton>
          <ZButton
            color="primary"
            className="min-w-[80px] min-h-[40px]"
            onClick={handleUpload}
            isLoading={isUploading}
            isDisabled={!isSaveButtonEnabled()}
            startContent={(!isUploading && saveStatus === 'idle') && <Upload size={16} />}
          >
            {saveStatus === 'success' ? (
              <Check />
            ) : "Save"}
          </ZButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default UploadDocumentsModal; 